import React, { useState, useEffect } from 'react';
import { responseHero } from '../../../../utils/axiosInterceptor';
import { getBMPDepositGroup } from '../../../../api/getBMPDepositGroup';
import { BuyingParamsResponse, getBMPBuyingDetails } from '../../../../api/getBMPFreeLiquidity';
import { BMP_DepositGroup } from '../../../../models/bmp.model';
import Decimal from 'decimal.js';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../store/reducers/userSlice';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const AddNewBuyModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
    const user = useSelector(selectUser);
    const [groups, setGroups] = useState<BMP_DepositGroup[]>([]);
    const [liquidityData, setLiquidityData] = useState<BuyingParamsResponse | null>(null);
    const [isLoadingLiquidity, setIsLoadingLiquidity] = useState<boolean>(false);
    
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        assetType: 1, // 1 = BTC, 2 = ETH
        exchange_rate: '',
        amount: '',
        quantity: '',
        note: '',
        depositGroupId: '',
    });

    useEffect(() => {
        if (isOpen) {
            getBMPDepositGroup().then(res => {
                setGroups(res);
                if (res.length > 0) {
                    setFormData(prev => ({ 
                        ...prev, 
                        depositGroupId: res[res.length - 1].id.toString() 
                    }));
                }
            });
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && formData.depositGroupId) {
            setIsLoadingLiquidity(true);
            getBMPBuyingDetails(Number(formData.depositGroupId))
                .then(res => {
                    setLiquidityData(res);
                    const safeNextLiquidity = res.nextAdvisedLiquidity ? new Decimal(res.nextAdvisedLiquidity).toDP(2).toString() : '';
                    setFormData(prev => ({
                        ...prev,
                        amount: safeNextLiquidity
                    }));
                })
                .catch(err => console.error("Likviditás lekérési hiba:", err))
                .finally(() => setIsLoadingLiquidity(false));
        }
    }, [isOpen, formData.depositGroupId]);

    useEffect(() => {
        const rate = formData.exchange_rate ? new Decimal(formData.exchange_rate) : new Decimal(0);
        const amt = formData.amount ? new Decimal(formData.amount) : new Decimal(0);

        if (rate.greaterThan(0) && amt.greaterThan(0)) {
            const calcQty = amt.div(rate).toDP(8).toString();
            if (formData.quantity !== calcQty) {
                setFormData(prev => ({ ...prev, quantity: calcQty }));
            }
        } else {
            if (formData.quantity !== '') {
                setFormData(prev => ({ ...prev, quantity: '' }));
            }
        }
    }, [formData.amount, formData.exchange_rate]);

    if (!isOpen) return null;

    const getAssetTicker = () => formData.assetType === 1 ? 'BTC' : 'ETH';
    
    const getAvgPurchasePrice = () => {
        if (!liquidityData) return '0';
        return formData.assetType === 1 
            ? liquidityData.avgBitcoinPurchasePrice 
            : liquidityData.avgEthereumPurchasePrice;
    };

    const maxLiquidity = liquidityData ? Number(liquidityData.totalFreeLiquidity) : Infinity;
    const isAmountOverMax = Number(formData.amount) > maxLiquidity;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const userId = user?.userInfo?.user_id;
        if (!userId) {
            alert("Hiba: Nincs bejelentkezett felhasználó!");
            return;
        }

        if (isAmountOverMax) {
            alert("Hiba: A megadott összeg meghaladja a maximálisan elérhető kereskedési likviditást!");
            return;
        }

        try {
            const finalDate = new Date(formData.date);
            
            const payload = {
                date: finalDate.toISOString(),
                operation: true,
                assetType: formData.assetType,
                quantity: Number(formData.quantity) || 0,
                exchange_rate: Number(formData.exchange_rate) || 0,
                amount: Number(formData.amount) || 0,
                note: formData.note,
                depositGroupId: Number(formData.depositGroupId),
                userId: userId
            };

            await responseHero.post(`${process.env.REACT_APP_API_URL}/bmp/trading/crypto`, payload);
            
            setFormData(prev => ({ 
                ...prev, 
                quantity: '', 
                amount: '', 
                note: '',
                exchange_rate: '' 
            }));
            
            onSuccess();
        } catch (err: any) {
            console.error("Backend hiba:", err.response?.data);
            alert(`Hiba a mentés során: ${err.response?.data?.message || "Ismeretlen hiba"}`);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md my-8 overflow-hidden animate-in zoom-in-95 duration-200">
                
                {/* Header */}
                <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                    <div>
                        <h3 className="text-lg font-black text-slate-800 tracking-tight">Kriptovaluta Vásárlás</h3>
                        <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Kizárólag vétel rögzítése</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl transition-colors leading-none">&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-5">
                    
                    {/* SECTION 1: Eszköz és Csoport választás */}
                    <div className="space-y-4">
                        {/* ASSET VÁLASZTÁS */}
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-400 mb-1.5">Választott Eszköz</label>
                            <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
                                <button 
                                    type="button" 
                                    onClick={() => setFormData({...formData, assetType: 1})} 
                                    className={`flex-1 py-2 rounded-lg text-xs font-black uppercase transition-all flex items-center justify-center gap-1.5 ${formData.assetType === 1 ? 'bg-amber-500 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-200'}`}
                                >
                                    <span>Bitcoin</span>
                                    <span className={`text-[9px] font-bold opacity-80`}>BTC</span>
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => setFormData({...formData, assetType: 2})} 
                                    className={`flex-1 py-2 rounded-lg text-xs font-black uppercase transition-all flex items-center justify-center gap-1.5 ${formData.assetType === 2 ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-200'}`}
                                >
                                    <span>Ethereum</span>
                                    <span className={`text-[9px] font-bold opacity-80`}>ETH</span>
                                </button>
                            </div>
                        </div>

                        {/* Célcsoport */}
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Célcsoport</label>
                            <select 
                                required
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-700 outline-none focus:ring-2 ring-emerald-500 transition-all"
                                value={formData.depositGroupId}
                                onChange={(e) => setFormData({...formData, depositGroupId: e.target.value})}
                            >
                                <option value="" disabled>Válassz csoportot...</option>
                                {groups.map(g => (
                                    <option key={g.id} value={g.id}>#{g.id} befektetési csoport</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="border-t border-slate-100 my-2" />

                    {/* SECTION 2: Pénzügyi adatok */}
                    <div className="space-y-4">
                        {/* Vételi Árfolyam */}
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-[10px] font-black uppercase text-slate-400">Vételi Árfolyam (USD)</label>
                                {liquidityData && (
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, exchange_rate: new Decimal(getAvgPurchasePrice()).toDP(2).toString() }))}
                                        className="text-[10px] text-indigo-600 font-bold hover:underline"
                                    >
                                        Csoport átlagár: ${Number(getAvgPurchasePrice()).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </button>
                                )}
                            </div>
                            <input 
                                type="number" 
                                step="any" 
                                required 
                                placeholder="0.00"
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-bold outline-none focus:ring-2 ring-emerald-500 transition-all" 
                                value={formData.exchange_rate} 
                                onChange={(e) => setFormData({...formData, exchange_rate: e.target.value})} 
                            />
                        </div>

                        {/* Vásárlási Összeg */}
                        <div className="p-3.5 rounded-xl bg-emerald-50/40 border border-emerald-100 space-y-2.5">
                            <div>
                                <label className="block text-[10px] font-black uppercase mb-1 text-emerald-700">Vásárlási Összeg (USD)</label>
                                <input 
                                    type="number" 
                                    step="any" 
                                    required 
                                    className={`w-full bg-white border rounded-xl px-3.5 py-2.5 text-sm font-black outline-none focus:ring-2 ${isAmountOverMax ? 'border-rose-500 ring-rose-500 text-rose-600' : 'border-emerald-200 ring-emerald-500 text-slate-800'}`} 
                                    value={formData.amount} 
                                    onChange={(e) => setFormData({...formData, amount: e.target.value})} 
                                />
                                {isAmountOverMax && (
                                    <p className="text-[10px] text-rose-600 font-bold mt-1">Nincs ennyi elérhető likviditás a csoportban!</p>
                                )}
                            </div>

                            {liquidityData && (
                                <div className="grid grid-cols-2 gap-2 text-[10px]">
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, amount: new Decimal(liquidityData.nextAdvisedLiquidity).toDP(2).toString() }))}
                                        className="p-1.5 bg-white border border-emerald-100 rounded-lg hover:border-emerald-400 transition-all text-center"
                                    >
                                        <span className="text-slate-400 font-medium">Ajánlott: </span>
                                        <span className="font-bold text-emerald-700">${Number(liquidityData.nextAdvisedLiquidity).toLocaleString('en-US', { minimumFractionDigits: 0 })}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, amount: new Decimal(liquidityData.totalFreeLiquidity).toDP(2).toString() }))}
                                        className="p-1.5 bg-white border border-emerald-100 rounded-lg hover:border-emerald-400 transition-all text-center"
                                    >
                                        <span className="text-slate-400 font-medium">Max: </span>
                                        <span className="font-bold text-slate-700">${Number(liquidityData.totalFreeLiquidity).toLocaleString('en-US', { minimumFractionDigits: 0 })}</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Számított mennyiség */}
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Számított Mennyiség ({getAssetTicker()})</label>
                            <input 
                                type="text" 
                                disabled 
                                placeholder="0.00000000"
                                className="w-full bg-slate-100 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-500 cursor-not-allowed" 
                                value={formData.quantity} 
                            />
                        </div>
                    </div>

                    <div className="border-t border-slate-100 my-2" />

                    {/* SECTION 3: Időpont és megjegyzés */}
                    <div className="space-y-4">
                        {/* Dátum */}
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Tranzakció Dátuma</label>
                            <input 
                                type="date" 
                                required 
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-bold outline-none focus:ring-2 ring-emerald-500 transition-all" 
                                value={formData.date} 
                                onChange={(e) => setFormData({...formData, date: e.target.value})} 
                            />
                        </div>

                        {/* Megjegyzés */}
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Megjegyzés</label>
                            <textarea 
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 ring-emerald-500 transition-all"
                                rows={2}
                                placeholder="Tranzakció részletei..."
                                value={formData.note}
                                onChange={(e) => setFormData({...formData, note: e.target.value})}
                            />
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-2">
                        <button 
                            type="submit" 
                            disabled={isAmountOverMax || isLoadingLiquidity}
                            className={`w-full py-3.5 rounded-xl font-black uppercase tracking-wider text-xs text-white transition-all shadow-md active:scale-98 ${isAmountOverMax || isLoadingLiquidity ? 'bg-slate-300 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                        >
                            {isLoadingLiquidity ? 'Adatok betöltése...' : 'Vétel Rögzítése'}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};